import { Phone, ArrowUpRight, Quote } from "lucide-react";
import { Link } from "@/components/common/Link";
const NAVY = "#081438";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F0EBE0";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const FACTS = [{
  num: "15+",
  label: "Years replacing garage door cables in Sydney",
  tag: "EXPERIENCE"
}, {
  num: "2,800+",
  label: "Cable jobs completed — residential & commercial",
  tag: "JOBS"
}, {
  num: "Always",
  label: "Replaced in pairs for safety & balance",
  tag: "STANDARD"
}, {
  num: "12mo",
  label: "Workmanship warranty on every cable repair",
  tag: "GUARANTEE"
}];
const SYMPTOMS = [{
  issue: "Cable snapped completely",
  cause: "Rust fatigue, overloaded spring, or end-of-life wear"
}, {
  issue: "Door sits crooked / one side down",
  cause: "One cable stretched or snapped — uneven tension"
}, {
  issue: "Loud bang then door jammed",
  cause: "Cable snapped under load — door dropped to safety stop"
}, {
  issue: "Visible fraying or rust",
  cause: "Cable nearing failure — replace before it breaks"
}, {
  issue: "Cable off the drum",
  cause: "Worn drum, broken spring, or cable too long/short"
}, {
  issue: "Grinding when opening",
  cause: "Cable wrapping incorrectly — risk of total snap"
}];
export default function CableIntro() {
  return <section style={{
    background: CREAM,
    position: "relative",
    overflow: "hidden",
    padding: "120px 24px",
    fontFamily: FONT_BODY
  }}>
      {/* Decorative watermark */}
      <div aria-hidden style={{
      position: "absolute",
      top: 60,
      right: -40,
      fontFamily: FONT_HEAD,
      fontSize: "clamp(180px, 22vw, 320px)",
      fontWeight: 900,
      color: CREAM_DEEP,
      opacity: 0.55,
      letterSpacing: "-0.04em",
      lineHeight: 1,
      pointerEvents: "none",
      userSelect: "none",
      zIndex: 0
    }}>
        CABLES
      </div>

      {/* Hairline gold rule top */}
      <div aria-hidden style={{
      position: "absolute",
      top: 80,
      left: 24,
      width: 120,
      height: 1,
      background: GOLD,
      zIndex: 1
    }} />

      <div style={{
      position: "relative",
      zIndex: 2,
      maxWidth: 1200,
      margin: "0 auto"
    }}>
        {/* === EDITORIAL HEADER === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
        gap: 60,
        marginBottom: 80,
        alignItems: "end"
      }} className="cable-intro-header">
          <div>
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 24
          }}>
              <span style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD_DARK
            }}>
                01 / About cable repair
              </span>
              <span style={{
              width: 32,
              height: 1,
              background: GOLD
            }} />
            </div>

            <h2 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(34px, 4.5vw, 56px)",
            fontWeight: 800,
            color: INK,
            lineHeight: 1.05,
            letterSpacing: "-0.025em",
            margin: 0
          }}>
              Cables are{" "}
              <span style={{
              color: GOLD_DARK,
              fontStyle: "italic",
              fontWeight: 700
            }}>
                the silent part
              </span>{" "}
              holding your door up.
            </h2>
          </div>

          {/* Pull quote */}
          <div style={{
          borderLeft: `2px solid ${GOLD}`,
          paddingLeft: 28,
          paddingTop: 8,
          paddingBottom: 8
        }}>
            <Quote size={28} color={GOLD} strokeWidth={2} style={{
            marginBottom: 12,
            opacity: 0.6
          }} />
            <p style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(17px, 1.5vw, 21px)",
            fontWeight: 500,
            color: INK,
            lineHeight: 1.5,
            margin: 0,
            fontStyle: "italic"
          }}>
              A snapped cable is one of the most dangerous garage door failures —
              the door can drop violently. Never try to lift it manually. Call
              us first.
            </p>
            <div style={{
            marginTop: 16,
            fontFamily: FONT_BODY,
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            color: INK_MUTED
          }}>
              — Gold Garage Door · Safety First
            </div>
          </div>
        </div>

        {/* === BODY: 2-COL ASYMMETRIC === */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
        gap: 64,
        marginBottom: 100,
        alignItems: "start"
      }} className="cable-intro-body">
          {/* LEFT — symptoms list */}
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: 24
          }}>
              Cable failure signs
            </div>

            <h3 style={{
            fontFamily: FONT_HEAD,
            fontSize: "clamp(24px, 2.4vw, 32px)",
            fontWeight: 700,
            color: INK,
            lineHeight: 1.2,
            letterSpacing: "-0.015em",
            margin: "0 0 32px"
          }}>
              Six warning signs we see every week
            </h3>

            <div style={{
            display: "flex",
            flexDirection: "column"
          }}>
              {SYMPTOMS.map((s, i) => <div key={i} style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 24,
              padding: "20px 0",
              borderTop: i === 0 ? `1px solid ${CREAM_DEEP}` : "none",
              borderBottom: `1px solid ${CREAM_DEEP}`,
              alignItems: "baseline"
            }}>
                  <span style={{
                fontFamily: FONT_HEAD,
                fontSize: 14,
                fontWeight: 800,
                color: GOLD_DARK,
                letterSpacing: "0.08em",
                minWidth: 28
              }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: 17,
                  fontWeight: 700,
                  color: INK,
                  marginBottom: 4,
                  letterSpacing: "-0.01em"
                }}>
                      {s.issue}
                    </div>
                    <div style={{
                  fontFamily: FONT_BODY,
                  fontSize: 14,
                  color: INK_MUTED,
                  lineHeight: 1.55
                }}>
                      {s.cause}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>

          {/* RIGHT — Facts stack */}
          <div>
            <div style={{
            fontFamily: FONT_BODY,
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: GOLD_DARK,
            marginBottom: 24
          }}>
              By the numbers
            </div>

            <div style={{
            display: "flex",
            flexDirection: "column",
            gap: 4
          }}>
              {FACTS.map((f, i) => <div key={i} style={{
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: 24,
              padding: "28px 28px 28px 0",
              borderBottom: i === FACTS.length - 1 ? "none" : `1px solid ${CREAM_DEEP}`,
              alignItems: "baseline"
            }}>
                  <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "clamp(36px, 3.8vw, 52px)",
                fontWeight: 800,
                color: INK,
                lineHeight: 1,
                letterSpacing: "-0.03em",
                minWidth: 120
              }}>
                    {f.num}
                  </div>
                  <div>
                    <div style={{
                  fontFamily: FONT_BODY,
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: "0.18em",
                  textTransform: "uppercase",
                  color: GOLD_DARK,
                  marginBottom: 8
                }}>
                      {f.tag}
                    </div>
                    <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: 15,
                  fontWeight: 500,
                  color: INK,
                  lineHeight: 1.45
                }}>
                      {f.label}
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* === NAVY TRUST STRIP === */}
        <div style={{
        position: "relative",
        background: NAVY,
        borderRadius: 4,
        padding: "40px 48px",
        overflow: "hidden"
      }}>
          <div aria-hidden style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: 4,
          height: "100%",
          background: GOLD
        }} />

          <div style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.4fr) auto",
          gap: 40,
          alignItems: "center"
        }} className="cable-intro-trust">
            <div>
              <div style={{
              fontFamily: FONT_BODY,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: "0.2em",
              textTransform: "uppercase",
              color: GOLD,
              marginBottom: 12
            }}>
                Cable broken right now?
              </div>
              <div style={{
              fontFamily: FONT_HEAD,
              fontSize: "clamp(20px, 2vw, 26px)",
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.3,
              letterSpacing: "-0.01em"
            }}>
                Don't force the door open. Call us — we'll secure it safely and
                quote on the spot.
              </div>
            </div>

            <div style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12
          }}>
              <a href="tel:0485000933" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              padding: "16px 24px",
              background: GOLD,
              color: NAVY,
              fontFamily: FONT_HEAD,
              fontSize: 15,
              fontWeight: 700,
              borderRadius: 8,
              textDecoration: "none",
              whiteSpace: "nowrap",
              transition: "transform 0.2s, box-shadow 0.2s",
              boxShadow: "0 6px 20px rgba(245,166,35,0.25)"
            }} onMouseEnter={e => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 10px 28px rgba(245,166,35,0.4)";
            }} onMouseLeave={e => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(245,166,35,0.25)";
            }}>
                <Phone size={16} strokeWidth={2.5} />
                0485 000 933
              </a>

              <Link to="#contact-us" style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "16px 24px",
              background: "transparent",
              color: "#ffffff",
              fontFamily: FONT_HEAD,
              fontSize: 15,
              fontWeight: 600,
              borderRadius: 8,
              textDecoration: "none",
              border: "1px solid rgba(255,255,255,0.25)",
              whiteSpace: "nowrap",
              transition: "background 0.2s, border-color 0.2s"
            }} onMouseEnter={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "rgba(255,255,255,0.08)";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.5)";
            }} onMouseLeave={e => {
              (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
              (e.currentTarget as HTMLAnchorElement).style.borderColor = "rgba(255,255,255,0.25)";
            }}>
                Get free quote
                <ArrowUpRight size={16} strokeWidth={2.5} />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 900px) {
          .cable-intro-header {
            grid-template-columns: 1fr !important;
            gap: 36px !important;
          }
          .cable-intro-body {
            grid-template-columns: 1fr !important;
            gap: 48px !important;
          }
          .cable-intro-trust {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
          }
        }
      `}</style>
    </section>;
}